
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product, OptionValue, ProductAddon, CartItem, User, Review, QNAItem } from '../types';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, doc, deleteDoc, orderBy, getDocs, updateDoc } from 'firebase/firestore';

interface ProductDetailPageProps {
  products: Product[];
  onAddToCart: (item: Omit<CartItem, 'cartId'>, startRect?: DOMRect) => void;
  currentUser: User | null;
  isAdmin: boolean;
}

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ products, onAddToCart, currentUser, isAdmin }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, OptionValue>>({});
  const [selectedAddOns, setSelectedAddOns] = useState<ProductAddon[]>([]);
  const [quantity, setQuantity] = useState(1);
  
  // Section Refs for Scrolling
  const detailRef = useRef<HTMLDivElement>(null);
  const refundRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  const qnaRef = useRef<HTMLDivElement>(null);
  
  // Reviews & QnA
  const [reviews, setReviews] = useState<Review[]>([]);
  const [qnas, setQnas] = useState<QNAItem[]>([]);
  const [newReviewContent, setNewReviewContent] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [newQnaContent, setNewQnaContent] = useState('');

  // Admin Reply State
  const [replyText, setReplyText] = useState('');
  const [activeReplyId, setActiveReplyId] = useState<{id: string, type: 'review'|'qna'} | null>(null);

  // [Fix Request 2] Improved Product Loading Logic
  useEffect(() => {
    const found = products.find(p => p.id === id);
    if (found) {
      setProduct(found);
      setSelectedOptions({});
      setSelectedAddOns([]);
      setQuantity(1);
    } else if (products.length > 0) {
      setProduct(null);
    }
  }, [id, products]);

  useEffect(() => {
    if (!id) return;

    const qReviews = query(collection(db, "reviews"), where("productId", "==", id));
    const unsubReviews = onSnapshot(qReviews, (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Review));
        data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setReviews(data);
    });

    const qQnas = query(collection(db, "qnas"), where("productId", "==", id));
    const unsubQnas = onSnapshot(qQnas, (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QNAItem));
        data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setQnas(data);
    });

    return () => {
        unsubReviews();
        unsubQnas();
    }
  }, [id]);

  const processDescription = (html: string) => {
    if (!html) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const iframes = doc.querySelectorAll('iframe');
      let modified = false;

      iframes.forEach(iframe => {
        const src = iframe.getAttribute('src');
        if (src && (src.includes('youtube.com') || src.includes('youtu.be'))) {
          try {
            const urlObj = new URL(src);
            if (!urlObj.searchParams.get('origin')) {
              urlObj.searchParams.set('origin', window.location.origin);
              iframe.setAttribute('src', urlObj.toString());
              modified = true;
            }
            if (iframe.getAttribute('referrerpolicy') !== 'strict-origin-when-cross-origin') {
              iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
              modified = true;
            }
            const requiredAllow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            if (iframe.getAttribute('allow') !== requiredAllow) {
              iframe.setAttribute('allow', requiredAllow);
              modified = true;
            }
            if (!iframe.hasAttribute('allowfullscreen')) {
              iframe.setAttribute('allowfullscreen', '');
              modified = true;
            }
          } catch (e) {}
        }
      });

      return modified ? doc.body.innerHTML : html;
    } catch (e) {
      return html;
    }
  };

  if (!product) {
    if (products.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-bold animate-pulse">Loading Product...</p>
            </div>
        );
    } else {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
                <div className="text-6xl mb-4">🚫</div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">상품을 찾을 수 없습니다.</h2>
                <p className="text-gray-500 font-medium mb-6">삭제되었거나 존재하지 않는 상품 경로입니다.</p>
                <button onClick={() => navigate('/shop')} className="px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition">
                    쇼핑홈으로 이동
                </button>
            </div>
        );
    }
  }

  const handleOptionChange = (optionId: string, value: OptionValue) => {
    setSelectedOptions(prev => ({ ...prev, [optionId]: value }));
  };

  const handleAddonToggle = (addon: ProductAddon) => {
    if (selectedAddOns.find(a => a.id === addon.id)) {
      setSelectedAddOns(prev => prev.filter(a => a.id !== addon.id));
    } else {
      setSelectedAddOns(prev => [...prev, addon]);
    }
  };

  const calculateTotalPrice = () => {
    let total = product.price;
    Object.values(selectedOptions).forEach((opt: OptionValue) => total += opt.extraPrice);
    selectedAddOns.forEach(addon => total += addon.price);
    return total * quantity;
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    const requiredOptions = product.options || [];
    const missingOptions = requiredOptions.filter(opt => !selectedOptions[opt.id]);
    
    if (missingOptions.length > 0) {
      if (window.customAlert) window.customAlert('모든 필수 옵션을 선택해주세요.');
      else alert('모든 필수 옵션을 선택해주세요.');
      return;
    }

    const item: Omit<CartItem, 'cartId'> = {
      ...product,
      quantity,
      selectedOptions,
      selectedAddOns
    };

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    onAddToCart(item, rect);
  };

  const handleSubmitReview = async () => {
    if (!currentUser) {
        if (window.customAlert) await window.customAlert('로그인이 필요합니다.');
        else alert('로그인이 필요합니다.');
        return;
    }

    let hasPurchased = false;
    try {
        const q = query(collection(db, "orders"), where("userId", "==", currentUser.id));
        const snap = await getDocs(q);
        snap.forEach(d => {
            const data = d.data();
            if (['PAYMENT_COMPLETE', 'PREPARING', 'SHIPPING', 'DELIVERED'].includes(data.status)) {
                if (data.items && Array.isArray(data.items)) {
                    if (data.items.some((i: any) => i.id === product.id)) {
                        hasPurchased = true;
                    }
                }
            }
        });
    } catch (e) {
        console.error("Purchase check failed:", e);
    }

    if (!hasPurchased && !isAdmin) {
        if (window.customAlert) await window.customAlert('상품을 구매한 고객님만 리뷰를 작성할 수 있습니다.');
        else alert('상품을 구매한 고객님만 리뷰를 작성할 수 있습니다.');
        return;
    }

    if (!newReviewContent.trim()) {
        if (window.customAlert) await window.customAlert('내용을 입력해주세요.');
        else alert('내용을 입력해주세요.');
        return;
    }

    try {
        await addDoc(collection(db, "reviews"), {
            productId: product.id,
            userId: currentUser.id,
            userName: currentUser.name,
            rating: newRating,
            content: newReviewContent,
            status: '답변대기',
            createdAt: new Date().toISOString()
        });
        setNewReviewContent('');
        setNewRating(5);
        if (window.customAlert) await window.customAlert('리뷰가 등록되었습니다.');
        else alert('리뷰가 등록되었습니다.');
    } catch (e) {
        console.error(e);
        if (window.customAlert) await window.customAlert('오류가 발생했습니다.');
        else alert('오류가 발생했습니다.');
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
      const confirmed = window.customConfirm ? await window.customConfirm('삭제하시겠습니까?') : window.confirm('삭제하시겠습니까?');
      if(!confirmed) return;
      await deleteDoc(doc(db, "reviews", reviewId));
  };

  const handleSubmitQna = async () => {
    if (!currentUser) {
        if (window.customAlert) await window.customAlert('로그인이 필요합니다.');
        else alert('로그인이 필요합니다.');
        return;
    }
    if (!newQnaContent.trim()) {
        if (window.customAlert) await window.customAlert('내용을 입력해주세요.');
        else alert('내용을 입력해주세요.');
        return;
    }

    try {
        await addDoc(collection(db, "qnas"), {
            productId: product.id,
            userId: currentUser.id,
            userName: currentUser.name,
            content: newQnaContent,
            status: '답변대기',
            createdAt: new Date().toISOString()
        });
        setNewQnaContent('');
        if (window.customAlert) await window.customAlert('문의가 등록되었습니다.');
        else alert('문의가 등록되었습니다.');
    } catch (e) {
        console.error(e);
        if (window.customAlert) await window.customAlert('오류가 발생했습니다.');
        else alert('오류가 발생했습니다.');
    }
  };

  const handleDeleteQna = async (qnaId: string) => {
    const confirmed = window.customConfirm ? await window.customConfirm('삭제하시겠습니까?') : window.confirm('삭제하시겠습니까?');
    if(!confirmed) return;
    await deleteDoc(doc(db, "qnas", qnaId));
  };

  const handleAdminReplySubmit = async () => {
      if (!activeReplyId || !replyText.trim()) return;
      
      try {
          await updateDoc(doc(db, activeReplyId.type === 'review' ? 'reviews' : 'qnas', activeReplyId.id), {
              answer: replyText,
              status: '답변완료',
              answeredAt: new Date().toISOString()
          });
          setReplyText('');
          setActiveReplyId(null);
          if (window.customAlert) await window.customAlert('답변이 등록되었습니다.');
          else alert('답변이 등록되었습니다.');
      } catch (e) {
          console.error(e);
          if (window.customAlert) await window.customAlert('오류가 발생했습니다.');
          else alert('오류가 발생했습니다.');
      }
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = ref.current.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
  };

  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountRate = hasDiscount ? Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100) : 0;

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Product Info Section */}
        <div className="flex flex-col md:flex-row gap-8 md:gap-16 mb-16">
            <div className="w-full md:w-1/2">
                <div className="aspect-square rounded-[32px] overflow-hidden bg-gray-50 border border-gray-100 shadow-sm relative group">
                    <img src={product.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={product.name} />
                    {hasDiscount && (
                        <div className="absolute top-6 left-6 flex flex-col gap-2">
                             <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-indigo-500 text-white text-xs font-black rounded-full shadow-lg uppercase tracking-widest animate-pulse">
                                SALE {discountRate}%
                             </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full md:w-1/2 flex flex-col">
                <div className="mb-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                        {product.categories.map(c => (
                            <span key={c} className="px-2 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-black rounded uppercase tracking-wider">{c}</span>
                        ))}
                    </div>
                    <h1 className="text-2xl md:text-4xl font-black text-gray-900 leading-tight mb-2">{product.name}</h1>
                    <p className="text-sm text-gray-400 font-medium">품번: {product.id}</p>
                </div>

                <div className="p-6 bg-gray-50 rounded-[24px] mb-8 border border-gray-100">
                    <div className="flex flex-col">
                        {hasDiscount && (
                            <span className="text-sm font-bold text-gray-400 line-through mb-1">{product.originalPrice?.toLocaleString()}원</span>
                        )}
                        <div className="flex items-center gap-2">
                            <span className={`text-3xl font-black ${hasDiscount ? 'text-indigo-600' : 'text-gray-900'}`}>{product.price.toLocaleString()}원</span>
                            {hasDiscount && <span className="text-sm font-black text-blue-600">{discountRate}% OFF</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-2 font-medium">배송비 {product.shippingFee === 0 ? '무료' : `${product.shippingFee.toLocaleString()}원 (100,000원 이상 무료)`}</p>
                    </div>
                </div>

                {product.options && product.options.length > 0 && (
                    <div className="space-y-4 mb-6">
                        {product.options.map(opt => (
                            <div key={opt.id}>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{opt.name}</label>
                                <div className="relative">
                                    <select 
                                        className="w-full p-4 bg-white border border-gray-200 rounded-2xl text-sm font-bold outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 transition appearance-none"
                                        onChange={(e) => {
                                            const val = opt.values.find(v => v.name === e.target.value);
                                            if (val) handleOptionChange(opt.id, val);
                                        }}
                                        value={selectedOptions[opt.id]?.name || ''}
                                    >
                                        <option value="" disabled>선택해주세요</option>
                                        {opt.values.map(v => (
                                            <option key={v.name} value={v.name}>
                                                {v.name} {v.extraPrice > 0 ? `(+${v.extraPrice.toLocaleString()}원)` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 9l-7 7-7-7" strokeWidth={2.5}/></svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {product.addOns && product.addOns.length > 0 && (
                    <div className="space-y-3 mb-6">
                         <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">추가 구성 상품</label>
                         {product.addOns.map(addon => (
                             <label key={addon.id} className={`flex items-center justify-between p-4 border rounded-2xl cursor-pointer transition ${selectedAddOns.find(a => a.id === addon.id) ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}>
                                 <div className="flex items-center gap-3">
                                     <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${selectedAddOns.find(a => a.id === addon.id) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                         {selectedAddOns.find(a => a.id === addon.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7" strokeWidth={3}/></svg>}
                                     </div>
                                     <input 
                                        type="checkbox" 
                                        checked={!!selectedAddOns.find(a => a.id === addon.id)} 
                                        onChange={() => handleAddonToggle(addon)}
                                        className="hidden"
                                     />
                                     <span className="text-sm font-bold text-gray-700">{addon.name}</span>
                                 </div>
                                 <span className="text-xs font-black text-indigo-600">+{addon.price.toLocaleString()}원</span>
                             </label>
                         ))}
                    </div>
                )}

                <div className="mt-auto pt-8 border-t border-gray-100">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100">
                            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 flex items-center justify-center bg-white rounded-xl text-gray-600 hover:text-indigo-600 font-black shadow-sm transition active:scale-90">-</button>
                            <span className="w-10 text-center font-black text-base">{quantity}</span>
                            <button onClick={() => setQuantity(quantity + 1)} className="w-9 h-9 flex items-center justify-center bg-white rounded-xl text-gray-600 hover:text-indigo-600 font-black shadow-sm transition active:scale-90">+</button>
                        </div>
                        <div className="text-right">
                             <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Price</span>
                             <span className="text-3xl font-black text-indigo-600 italic tracking-tighter">{calculateTotalPrice().toLocaleString()}원</span>
                        </div>
                    </div>

                    <div className="flex gap-3">
                         <button 
                            onClick={handleAddToCart}
                            disabled={product.stock <= 0}
                            className={`flex-1 py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition shadow-xl flex items-center justify-center gap-2 active:scale-95 ${product.stock <= 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-indigo-600'}`}
                         >
                            {product.stock <= 0 ? 'Sold Out' : (
                                <>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                                Add to Cart
                                </>
                            )}
                         </button>
                    </div>
                </div>
            </div>
        </div>

        <div className="sticky top-16 bg-white/95 backdrop-blur-md z-20 border-b border-gray-100 mb-10 shadow-sm">
            <div className="flex">
                <button onClick={() => scrollToSection(detailRef)} className="flex-1 py-4 text-xs md:text-sm font-black text-gray-500 hover:text-indigo-600 uppercase tracking-widest transition">상세정보</button>
                <button onClick={() => scrollToSection(refundRef)} className="flex-1 py-4 text-xs md:text-sm font-black text-gray-500 hover:text-indigo-600 uppercase tracking-widest transition">환불/반품</button>
                <button onClick={() => scrollToSection(reviewRef)} className="flex-1 py-4 text-xs md:text-sm font-black text-gray-500 hover:text-indigo-600 uppercase tracking-widest transition">리뷰 ({reviews.length})</button>
                <button onClick={() => scrollToSection(qnaRef)} className="flex-1 py-4 text-xs md:text-sm font-black text-gray-500 hover:text-indigo-600 uppercase tracking-widest transition">Q&A ({qnas.length})</button>
            </div>
        </div>

        {/* Detail Section */}
        <div ref={detailRef} className="scroll-mt-32 mb-16 min-h-[300px]">
            <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-8 uppercase tracking-tighter italic">Product Detail</h3>
            <div className="flex justify-center">
                <div 
                    className="prose prose-indigo max-w-4xl w-full prose-img:rounded-[32px] prose-img:shadow-lg prose-p:text-gray-600 prose-headings:font-black" 
                    dangerouslySetInnerHTML={{ __html: processDescription(product.description) }} 
                />
            </div>
        </div>

        {/* [위치 변경] Refund Policy Image Section: 상세정보와 리뷰 사이 */}
        <div ref={refundRef} className="scroll-mt-32 pt-16 pb-12 max-w-4xl mx-auto reveal-on-scroll">
            <div className="rounded-[40px] overflow-hidden shadow-2xl border border-gray-100">
                <img 
                    src="https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/refund.jpg?alt=media&token=abc3372b-f1e7-4d72-ad80-5ee5e15f23b5" 
                    alt="Refund Policy" 
                    className="w-full h-auto"
                />
            </div>
            <p className="mt-6 text-center text-xs font-bold text-gray-400 italic">
                메이커스토리는 공정한 거래 문화를 지향합니다. 환불 규정을 꼭 확인해주세요.
            </p>
        </div>

        <div className="border-t border-dashed border-gray-200 my-12"></div>

        {/* Review Section */}
        <div ref={reviewRef} className="scroll-mt-32 mb-16 md:mb-24 max-w-4xl mx-auto">
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tighter italic">Reviews</h3>
                    <p className="text-xs text-gray-400 font-bold mt-1">실제 구매 고객님들의 소중한 후기입니다.</p>
                </div>
                <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">{reviews.length}개의 리뷰</span>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm mb-8">
                 <div className="flex justify-between items-center mb-6">
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Write a Review</h3>
                    <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(star => (
                            <button key={star} onClick={() => setNewRating(star)} className={`text-2xl transition hover:scale-110 ${star <= newRating ? 'text-amber-400' : 'text-gray-200'}`}>★</button>
                        ))}
                    </div>
                 </div>
                 <textarea 
                    className="w-full p-5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 transition text-sm mb-4 font-medium"
                    rows={4}
                    placeholder="상품에 대한 솔직한 리뷰를 남겨주세요."
                    value={newReviewContent}
                    onChange={e => setNewReviewContent(e.target.value)}
                 />
                 <div className="text-right">
                     <button onClick={handleSubmitReview} className="px-8 py-3 bg-indigo-600 text-white font-black text-xs rounded-xl hover:bg-gray-900 transition shadow-lg">등록하기</button>
                 </div>
            </div>

            <div className="space-y-4">
                {reviews.length === 0 ? (
                    <div className="py-16 text-center bg-gray-50 rounded-[32px] text-gray-400 font-bold italic">아직 작성된 리뷰가 없습니다. 첫 리뷰를 남겨주세요!</div>
                ) : reviews.map(review => (
                    <div key={review.id} className="p-6 md:p-8 bg-white border border-gray-100 rounded-[32px] hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <div className="flex text-amber-400 text-sm">{'★'.repeat(review.rating)}</div>
                                <div className="h-4 w-px bg-gray-200"></div>
                                <span className="font-black text-gray-900 text-sm">{review.userName}</span>
                                <span className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString()}</span>
                            </div>
                            {(isAdmin || currentUser?.id === review.userId) && (
                                <button onClick={() => handleDeleteReview(review.id)} className="p-2 text-gray-300 hover:text-red-500 transition">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth={2}/></svg>
                                </button>
                            )}
                        </div>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line font-medium pl-1">{review.content}</p>
                        {review.answer && (
                            <div className="mt-6 p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100/50 relative ml-4">
                                <div className="absolute top-0 left-6 -translate-y-1/2 bg-indigo-100 text-indigo-600 text-[10px] font-black px-2 py-0.5 rounded uppercase shadow-sm">Admin Reply</div>
                                <p className="text-sm text-indigo-900 leading-relaxed font-medium">{review.answer}</p>
                            </div>
                        )}
                        
                        {isAdmin && !review.answer && (
                            <div className="mt-4 pl-4 border-l-2 border-gray-100">
                                {activeReplyId?.id === review.id && activeReplyId.type === 'review' ? (
                                    <div className="flex gap-2 items-start animate-in fade-in slide-in-from-top-2">
                                        <textarea 
                                            className="flex-1 p-3 bg-gray-50 rounded-xl text-xs font-medium border border-gray-200 outline-none focus:border-indigo-500 transition"
                                            rows={2}
                                            placeholder="관리자 답변을 입력하세요..."
                                            value={replyText}
                                            onChange={e => setReplyText(e.target.value)}
                                        />
                                        <div className="flex flex-col gap-1">
                                            <button onClick={handleAdminReplySubmit} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black hover:bg-indigo-700 shadow-sm">등록</button>
                                            <button onClick={() => { setActiveReplyId(null); setReplyText(''); }} className="px-3 py-2 bg-gray-200 text-gray-500 rounded-lg text-[10px] font-black hover:bg-gray-300">취소</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => { setActiveReplyId({id: review.id, type: 'review'}); setReplyText(''); }}
                                        className="text-[10px] font-black text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition"
                                    >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                        답변 달기
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>

        <div className="border-t border-dashed border-gray-200 my-12"></div>

        {/* QnA Section */}
        <div ref={qnaRef} className="scroll-mt-32 mb-16 max-w-4xl mx-auto">
             <div className="flex justify-between items-end mb-8">
                <div>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tighter italic">Q&A</h3>
                    <p className="text-xs text-gray-400 font-bold mt-1">상품에 대해 궁금한 점을 남겨주세요.</p>
                </div>
                <span className="text-xs font-black text-gray-900 bg-gray-100 px-3 py-1 rounded-full">{qnas.length}개의 문의</span>
            </div>

             <div className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm mb-8">
                 <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-6">Ask Question</h3>
                 <textarea 
                    className="w-full p-5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600 transition text-sm mb-4 font-medium"
                    rows={4}
                    placeholder="상품에 대해 궁금한 점을 문의해주세요."
                    value={newQnaContent}
                    onChange={e => setNewQnaContent(e.target.value)}
                 />
                 <div className="text-right">
                     <button onClick={handleSubmitQna} className="px-8 py-3 bg-gray-900 text-white font-black text-xs rounded-xl hover:bg-indigo-600 transition shadow-lg">문의하기</button>
                 </div>
            </div>

            <div className="space-y-4">
                {qnas.length === 0 ? (
                    <div className="py-16 text-center bg-gray-50 rounded-[32px] text-gray-400 font-bold italic">등록된 문의가 없습니다.</div>
                ) : qnas.map(qna => (
                    <div key={qna.id} className="p-6 md:p-8 bg-white border border-gray-100 rounded-[32px] hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide ${qna.status === '답변완료' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>{qna.status}</span>
                                <span className="font-black text-gray-900 text-sm">{qna.userName}</span>
                                <span className="text-xs text-gray-400">{new Date(qna.createdAt).toLocaleDateString()}</span>
                            </div>
                            {(isAdmin || currentUser?.id === qna.userId) && (
                                <button onClick={() => handleDeleteQna(qna.id)} className="p-2 text-gray-300 hover:text-red-500 transition">
                                     <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth={2}/></svg>
                                </button>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <span className="text-indigo-600 font-black text-lg shrink-0">Q.</span>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line font-medium flex-1 pt-0.5">{qna.content}</p>
                        </div>
                        {qna.answer && (
                            <div className="mt-6 flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                <span className="text-gray-900 font-black text-lg shrink-0">A.</span>
                                <p className="text-gray-600 leading-relaxed flex-1 pt-0.5 font-medium">{qna.answer}</p>
                            </div>
                        )}

                        {isAdmin && !qna.answer && (
                            <div className="mt-4 pl-4 border-l-2 border-gray-100">
                                {activeReplyId?.id === qna.id && activeReplyId.type === 'qna' ? (
                                    <div className="flex gap-2 items-start animate-in fade-in slide-in-from-top-2">
                                        <textarea 
                                            className="flex-1 p-3 bg-gray-50 rounded-xl text-xs font-medium border border-gray-200 outline-none focus:border-indigo-500 transition"
                                            rows={2}
                                            placeholder="관리자 답변을 입력하세요..."
                                            value={replyText}
                                            onChange={e => setReplyText(e.target.value)}
                                        />
                                        <div className="flex flex-col gap-1">
                                            <button onClick={handleAdminReplySubmit} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black hover:bg-indigo-700 shadow-sm">등록</button>
                                            <button onClick={() => { setActiveReplyId(null); setReplyText(''); }} className="px-3 py-2 bg-gray-200 text-gray-500 rounded-lg text-[10px] font-black hover:bg-gray-300">취소</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => { setActiveReplyId({id: qna.id, type: 'qna'}); setReplyText(''); }}
                                        className="text-[10px] font-black text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition"
                                    >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                        답변 달기
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    </div>
  );
};

export default ProductDetailPage;
